/**
 * Module dependencies.
 */

import { ReactNode } from 'react';
import { Spinner } from 'src/components/core/spinner';
import { useSession } from 'src/providers/session';
import { LayoutType } from 'src/types/app';

import { AuthenticationLayout } from './authentication';
import { BaseLayout } from './base';
import { CompleteSignUpLayout } from './complete-sign-up';
import { PageTransition } from './page-transition';
import { PdfLayout } from './pdf';
import { SettingsLayout } from './settings';
import { SurveyLayout } from './survey';

/**
 * `Props` type.
 */

type Props = {
  children?: ReactNode;
  isPrivate?: boolean;
  layout?: LayoutType;
};

/**
 * `layouts` constant.
 */

const layouts = {
  auth: AuthenticationLayout,
  base: BaseLayout,
  completeSignUp: CompleteSignUpLayout,
  pdf: PdfLayout,
  settings: SettingsLayout,
  survey: SurveyLayout
} as const;

/**
 * Export `Layout` component.
 */

export function Layout({ children, isPrivate, layout = 'base' }: Props) {
  const Layout = layouts?.[layout] ?? layouts.base;
  const { isError, user } = useSession();

  // After a client-side login, `HydrationBoundary` only hydrates the cached `me` query after render,
  // so wait for it instead of rendering private pages without the user. If it fails, render as before.
  const isLoadingUser = isPrivate && !user && !isError;

  return (
    <PageTransition>
      <Layout>{isLoadingUser ? <Spinner isActive size={'large'} /> : children}</Layout>
    </PageTransition>
  );
}
